import axios from 'axios'
import type { AxiosInstance, AxiosPromise, AxiosRequestConfig, AxiosStatic } from 'axios'
import axiosRetry from 'axios-retry'
import type { IAxiosRetryConfig } from 'axios-retry'

import { HTTP_METHOD } from './types'
import type {
  ApiConfig,
  ApiConstructorParams,
  CallOptions,
  ConfigGenerator,
  ServiceUrlBases,
} from './types'

const defaultRetryConfig: IAxiosRetryConfig = {
  retries: 4,
  retryDelay: axiosRetry.exponentialDelay,
  shouldResetTimeout: true,
}

class Api {
  configGenerator: ConfigGenerator

  client: AxiosInstance | AxiosStatic

  serviceUrlBases: ServiceUrlBases

  constructor(initParams: ApiConstructorParams) {
    this.configGenerator = initParams.configGenerator
    this.client = axios.create()
    this.serviceUrlBases = initParams.serviceUrlBases

    if (!initParams.axiosRetry?.disabled) {
      axiosRetry(this.client, {
        ...defaultRetryConfig,
        ...initParams.axiosRetry?.config,
      })
    }

    if (initParams.axiosInterceptorsCallback?.response) {
      this.client.interceptors.response.use(
        initParams.axiosInterceptorsCallback.response.onFulfilled,
        initParams.axiosInterceptorsCallback.response.onRejected
      )
    }

    if (initParams.axiosInterceptorsCallback?.request) {
      this.client.interceptors.request.use(
        initParams.axiosInterceptorsCallback.request.beforeSend,
        initParams.axiosInterceptorsCallback.request.onRejected
      )
    }
  }

  call = <CustomResponseData = any>(callOptions: CallOptions): AxiosPromise<CustomResponseData> => {
    if (callOptions.baseURL && callOptions.serviceName) {
      throw new Error('Either "baseURl" or "serviceName" should be provided, not both')
    } else if (callOptions.baseURL === undefined && callOptions.serviceName === undefined) {
      throw new Error('Either "baseURl" or "serviceName" must be provided')
    }

    if (this.configGenerator === undefined || this.client === undefined) {
      throw new Error('The API service is not initialized.')
    }

    const config: ApiConfig = this.configGenerator()

    const serviceNameNotFound =
      callOptions.serviceName &&
      !Object.keys(this.serviceUrlBases).includes(callOptions.serviceName)
    if (serviceNameNotFound) {
      throw new Error(
        `Service name ${callOptions.serviceName} is not found amongst provided service url bases.`
      )
    }

    const isInternalBaseURL =
      callOptions.baseURL &&
      Object.values(this.serviceUrlBases).some(
        (urlBase) => urlBase.url === callOptions.baseURL && urlBase.isInternal
      )

    const isInternalServiceName =
      callOptions.serviceName && this.serviceUrlBases[callOptions.serviceName].isInternal
    const headers = {
      ...(isInternalBaseURL || isInternalServiceName ? config.internalHeaders : {}),
      ...callOptions.headers,
    }

    const baseConfig: AxiosRequestConfig = {
      [callOptions.method === HTTP_METHOD.GET ? 'params' : 'data']: callOptions.payload,
      headers,
      baseURL: callOptions.serviceName
        ? this.serviceUrlBases[callOptions.serviceName].url
        : callOptions.baseURL,
      method: callOptions.method,
      url: callOptions.url,
    }

    return this.client(baseConfig)
  }
}
export default Api
